import { ellipsis } from "polished";
import styled, { css } from "styled-components";

import { LocalStackOrder, T_BOLD_tiny, T_PLAIN_big, T_TITLE_4, T_TITLE_4_NORMAL } from "../../global.style";
import { Theme } from "../../theme";
import { VIEW_PADDING_VALUE } from "../../views/View.styles";
import { ButtonGroup } from "../button";
import Clickable from "../clickable";
import { StyledDropArea } from "../fileUploader";
import { ColoredIcon } from "../icon";
import Switch from "../inputs/switch";
import TextArea from "../inputs/textArea";
import { StyledInput, StyledTextArea } from "../inputs/textArea/TextArea.styles";
import { Separator } from "../separator/Separator";
import { SeparatorLine } from "../separator/Separator.styles";
import { ChatMessageType } from "./ChatMessage.utils";

const ICON_SIZE = 38;
const MESSAGE_MARGIN = 24;
const BORDER_SIZE = 2;
const ICON_SIZE_WITH_BORDER = ICON_SIZE + 2 * BORDER_SIZE;

interface IType {
    $type: ChatMessageType;
}

function getColorByType(type: ChatMessageType): keyof Theme {
    switch (type) {
        case ChatMessageType.Received:
            return "C_SPLIT_LAYOUT_BUBBLE";
        case ChatMessageType.Sent:
            return "C_BTN_4L_trans_bg_selected";
    }
}

function isReceived(type: ChatMessageType): boolean {
    return type === ChatMessageType.Received;
}

export const StyledMessage = styled.div<IType>`
    position: relative;
    margin-bottom: ${MESSAGE_MARGIN}px;
    padding: ${ICON_SIZE_WITH_BORDER / 2}px 50px 0;
    width: 820px;
    max-width: 100%;

    ${props => isReceived(props.$type) ? css`
        padding-right: 100px;
        align-self: flex-start;
        text-align: left;
    ` : css`
        padding-left: 100px;
        align-self: flex-end;
        text-align: right;
    `};
`;

export const MessageIconWrapper = styled.div<{ $isDisabled?: boolean } & IType>`
    position: absolute;
    top: 0;
    flex: 0 0 auto;
    width: ${ICON_SIZE}px;
    height: ${ICON_SIZE}px;
    overflow: hidden;

    ${props => isReceived(props.$type) ? css`
        left: 0;
    ` : css`
        right: 0;
    `};

    border: ${BORDER_SIZE}px solid ${props => props.theme[getColorByType(props.$type)]};
    border-radius: 50%;
    background-color: ${props => props.theme.C_BG_menu};
    ${props => props.$isDisabled && css`opacity: .5`};

    svg {
        background-color: ${props => props.theme.C_BG_menu};
    }
`;

export const CenteredIcon = styled.div`
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    // in case 
    ${T_TITLE_4};
    text-transform: uppercase;
    color: ${props => props.theme.C_TEXT_primary};
`;

const CONTENT_HOR_PADDING = 16;

export const AttachmentWrapper = styled.div <{
    $hasText: boolean;
}>`
    margin-top: ${props => props.$hasText ? "16px" : ""};
`;

export const ContentWrapper = styled.div<IType>`
    position: relative;
    display: inline-block;
    ${T_TITLE_4_NORMAL};
    text-align: left;
    padding: 12px ${CONTENT_HOR_PADDING}px;
    max-width: 100%;
    overflow-wrap: break-word;
    white-space: pre-wrap;
    background-color: ${props => props.theme[getColorByType(props.$type)]};

    ${props => isReceived(props.$type) ? css`
        border-radius: 3px 20px 20px 20px;
    ` : css`
        border-radius: 20px 3px 20px 20px;
    `};
`;

export const MessageSubject = styled.h3`
    ${T_TITLE_4};
    margin-bottom: 12px;
`;

export const Header = styled.div<IType>`
    position: absolute;
    top: -3px;
    transform: translate(0, -100%);

    ${T_BOLD_tiny};
    color: ${props => props.theme.C_TEXT_primary};
    opacity: 0.5;
    min-width: fit-content;
    white-space: nowrap;

    ${props => isReceived(props.$type) ? css`
        left: 0
    ` : css`
        right: 0
    `};
`;

export const ChatMessageStreamStyled = styled.div`
    position: relative;
    height: 100%;
    min-width: 100%;
    width: fit-content;
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
    margin-top: 42px;
`;

export const ChatMessagesWrapper = styled.div`
    display: flex;
    flex-direction: column;
`;

export const UploadedFilesStyled = styled.div`
    display: flex;
    align-items: flex-start;
    flex-wrap: wrap;
    margin: 10px 0 10px 16px;
`;

export const ChatMessageAttachmentsStyled = styled.div`
    display: flex;
    align-items: flex-start;
    flex-wrap: wrap;
`;

export const ButtonGroupStyled = styled(ButtonGroup)`
    gap: 12px;
    flex: 1 0 0;
`;

export const InputWrapper = styled.div`
    flex: 1 1 100%;
    margin-right: 12px;
    padding-top: 6px;
    min-width: 220px;
`;

const MessageInputStyles = css`
    background: transparent;
    border: none;
    padding-left: 16px;
`;

export const SubjectInput = styled(TextArea)`
    ${StyledInput} {
        ${MessageInputStyles};

        &, &::placeholder {
            font-weight: bold;
        }
    }
`;

export const SeparatorStyled = styled(Separator)`
    margin: 6px 0 6px 16px;

    ${SeparatorLine} {
        height: 1px;
    }
`;

export const MessageInput = styled(TextArea)`
    ${StyledTextArea} {
        ${MessageInputStyles};
    }
`;

export const ClickableStyled = styled(Clickable)`
    margin: 0 3px;
    max-width: 100%;

    display: flex;
    flex-direction: row;
    align-items: center;

    ${ColoredIcon} {
        display: inline-block;
        vertical-align: text-bottom;
        color: ${props => props.theme.C_TEXT_primary};
    }
`;

export const MessageFileStyled = styled.span`
    display: inline-flex;
    ${T_PLAIN_big};
    margin-right: 3px;
    flex-wrap: nowrap;
    white-space: nowrap;
    max-width: 100%;

    &:not(:last-child)::after {
        content: ",";
    }
`;

export const ChatMessageInputStyled = styled.div`
    display: flex;
    align-items: center;
    justify-content: flex-end;
    z-index: ${LocalStackOrder.AboveContent};
    margin: 6px ${VIEW_PADDING_VALUE}px 0;
    flex-wrap: wrap;
    gap: 24px;
`;

export const ChatMessageInputBackground = styled.div`
    position: relative;
    flex: 1 1 auto;

    ${StyledDropArea} {
        display: flex;
        align-items: flex-end;
        flex-wrap: nowrap;
        padding: 3px;
        border-radius: 22px;
    }
    border-radius: 22px;
    background-color: ${props => props.theme.C_BG_menu};
    box-shadow: 0 0 16px 0 rgba(0, 0, 0, 0.12);
    min-height: 44px; // todo: remove
`;

export const SwitchStyled = styled(Switch)`
    flex: 0 0 auto;
`;

export const AttachmentText = styled.div `
    ${ellipsis()};
    max-width: calc(100% - 10px);
`;